/**
 * Message Loading State Handlers
 * 
 * Creates loading state management handlers for message operations
 * Used by new messages, edits, and regenerations
 */

import useGenerateApiSignal from "../../store/useGenerateApiSignal";
import useFeedbackStore from "../../store/useFeedbackStore";
import { DENIAL_ERROR_MESSAGES } from "../../utils/helpers";

/**
 * Create loading state handlers for message operations
 * 
 * @param {Object} params - Handler parameters
 * @param {Function} params.setIsLatestMessageLoading - Loading state setter
 * @param {Function} params.setGenerateStatus - Generate status setter
 * @param {Function} params.setRegenerateIndex - Regenerate index setter
 * @returns {Object} Loading state handlers
 */
export const createMessageLoadingHandlers = ({
  setIsLatestMessageLoading,
  setGenerateStatus,
  setRegenerateIndex,
  chat_id,
}) => {
  /**
   * Start loading state
   * @param {string} messageId - Message/prompt ID being generated
   * @param {number} index - Message index for regenerate tracking
   * @param {string|null} answerId - Answer currently being generated
   */
  const startLoading = (messageId, index = null, answerId = null) => {
    useGenerateApiSignal.getState().addSignal(chat_id, {
      generate_id: messageId,
      ...(answerId && { answer_id: answerId }),
    });
    setIsLatestMessageLoading(true);
    setGenerateStatus(true);
    if (index !== null) {
      setRegenerateIndex(index);
    }
  };

  /**
   * Stop loading state
   */
  const stopLoading = () => {
    useGenerateApiSignal.getState().removeSignal(chat_id);
    setIsLatestMessageLoading(false);
    setGenerateStatus(false);
    setRegenerateIndex(null);
  };

  /**
   * Complete cleanup with full state reset
   */
  const cleanup = () => {
    useGenerateApiSignal.getState().removeSignal(chat_id);
    setIsLatestMessageLoading(false);
    setGenerateStatus(false);
    setRegenerateIndex(null);
  };

  /**
   * Pause loading state for approval card display.
   * Clears the visible loading indicators (spinner, textarea disabled) but
   * intentionally preserves generate_id so the sidebar breathing dot stays
   * visible while the user is deciding on the approval card.
   * generate_id is cleared when the user confirms (startLoading re-sets it
   * for the resumed stream) or cancels/expires (stopLoading clears it).
   */
  const pauseForApproval = () => {
    setIsLatestMessageLoading(false);
    setGenerateStatus(false);
    setRegenerateIndex(null);
  };

  /**
   * Shared error handler for all generation flows.
   * AbortError means the fetch was intentionally cancelled on navigation —
   * the backend keeps running, so we must NOT clear the signal
   * (that would kill the sidebar breathing dot). Any other error is a real
   * failure and warrants full cleanup.
   *
   * Returns true when the error is fully handled (toast shown); callers
   * should skip writing an inline error message in that case.
   * Returns false when the caller should write the inline error itself.
   */
  const handleError = (error) => {
    if (error.name === 'AbortError') return true;
    if (error.code === 'generation_limit_reached') {
      useFeedbackStore.getState().showFeedback(DENIAL_ERROR_MESSAGES.generation_limit_reached(), "error");
      cleanup();
      return true;
    }
    cleanup();
    return false;
  };

  return {
    startLoading,
    stopLoading,
    cleanup,
    pauseForApproval,
    handleError,
  };
};
